import { useTranslation } from "react-i18next";
import {
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
  HeartOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Input, MenuProps, message, Modal } from "antd";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import UploadItem from "../../../../../../../components/course-construction/content-course-construction/statistic-course/components/comment/UploadItem";
import {
  DislikeActiveIcon,
  DislikeIcon,
  FeedbackIcon,
  LikeActiveIcon,
  LikeIcon
} from "../../../../../../../components/course-construction/content-course-construction/statistic-course/components/icons";
import {
  DISLIKE_REACTION,
  LIKE_REACTION,
} from "../../../../../../../constants";
import useUploadFile from "../../../../../../../hooks/useUploadFile";
import { removeDiscussion, saveDiscussion, seenComment, unSaveDiscussion } from "../../../../../../../service/course-management";
import { CourseManagementStore, useAuthStore } from "../../../../../../../stores/stores";
import {
  AttachmentProps,
  CommentsBeProps,
  ReactionCount,
} from "../../../../../../../types/course-construction";
import { displayDateComment } from "../../../../../../../utils/converDate";
import { getCleanText } from "../../../../../../../utils/format";
import "./style.scss";

export interface CommentProps extends CommentsBeProps {
  id: number;
  level: number;
  isReplying?: boolean;
  parent?: number;
  content?: string;
  replies: CommentProps[];
}

export interface ReplyValue {
  replyText: string;
  file: any;
  comment: CommentProps;
  attachmentRemoved?: number[];
}

interface ReplyProps {
  comment: CommentProps;
  replies?: CommentProps[];
  commentEditing?: CommentProps;
  onLike?: (value: CommentProps) => void;
  onDelete?: (value: CommentProps) => void;
  onDislike?: (value: CommentProps) => void;
  onSaveReply?: (value: ReplyValue) => void;
  onClickReply?: (value: CommentProps) => void;
  onSaveEditComment?: (value: ReplyValue) => void;
  onClickEdit?: (value: CommentProps | undefined) => void;
  replyingCommentId: any
}

const Reply = ({
  comment,
  replies,
  commentEditing,
  onLike = () => {},
  onDelete = () => { },
  onDislike = () => { },
  onSaveReply = () => { },
  onClickEdit = () => { },
  onClickReply = () => { },
  onSaveEditComment = () => { },
  replyingCommentId
}: ReplyProps) => {
  const { t } = useTranslation();
  const userInfo = useAuthStore((state) => state.user);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLDivElement>(null);
  const { reloadDiscussion, setReloadDiscussion, discussionMode } = CourseManagementStore()
  const [repliesLimit, setRepliesLimit] = useState<number>(0);
  const visibleReplies = (replies || []).slice(0, repliesLimit);
  const hasMoreReplies = (replies || []).length > repliesLimit;
  const [isViewFull, setIsViewFull] = useState<boolean>(false);
  const [editText, setEditText] = useState<string>("");
  const [addAttachments, setAddAttachments] = useState<any>([]);
  const [attachmentsRemoved, setAttachmentsRemoved] = useState<number[]>([]);
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [replyText, setReplyText] = useState<string>("");
  const textAreaRef = useRef<any>(null);
  const [addAttachmentsPreview, setAddAttachmentsPreview] = useState<any>([]);
  const [editAttachments, setEditAttachments] = useState<any>([]);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [lineHeight, setLineHeight] = useState<number>(0);
  const {
    filesPreview,
    discussionPayloadFiles,
    handleChangeFiles,
    handleDeleteFile,
    resetFiles,
    isCorrectFile,
  } = useUploadFile();

  const isEditing =
    commentEditing?.id === comment.id && commentEditing !== undefined;

  const countLike =
    comment.reactionCount?.find(
      (react: ReactionCount) => react.reactionType === LIKE_REACTION
    )?.count || 0;

  const countDislike =
    comment.reactionCount?.find(
      (react: ReactionCount) => react.reactionType === DISLIKE_REACTION
    )?.count || 0;

  const isLike = comment?.myReaction === LIKE_REACTION;
  const isDislike = comment?.myReaction === DISLIKE_REACTION;

  const attachments = comment?.attachments;


  const handleSaveReply = (reply: ReplyValue) => {
    onSaveReply(reply);
    setIsReplying(false);
    setReplyText("");
    resetFiles();
  };

  const removeDuplicate = (inputText: string, replaceString: string) => {
    const firstIndex = inputText.indexOf(replaceString);
    const beforeFirst = inputText.slice(0, firstIndex + replaceString.length);
    const afterFirst = inputText
      .slice(firstIndex + replaceString.length)
      .replace(new RegExp(replaceString, "g"), "");
    return beforeFirst + afterFirst;
  };

  const renderHighlightedText = (text: string, username: string) => {
    if (text?.includes(username)) {
      const parts = text.split(username);
      return (
        <span>
          {parts[0].endsWith("@") ? parts[0].slice(0, -1) : parts[0]}
          <span className="font-weight-6">{username}</span>
          {parts[1]}
        </span>
      );
    }
    return text;
  };

  useEffect(() => {
    if (comment?.content && comment?.content?.length < 200) {
      setIsViewFull(true);
      if (attachments && attachments?.length > 6) {
        setIsViewFull(false);
      }
    }
    if (
      !comment?.content &&
      attachments &&
      attachments?.length < 6 &&
      attachments?.length > 0
    ) {
      setIsViewFull(true);
    }
  }, [comment?.content, attachments]);

  useEffect(() => {
    setReplyText(`@${comment?.author?.username || comment?.author?.name}`);
  }, [comment?.author?.username, isReplying]);

  useEffect(() => {
    if (textAreaRef.current) {
      const textarea = textAreaRef.current.resizableTextArea
        .textArea as HTMLTextAreaElement;
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      textarea.focus();
    }
    const targetElement = document.getElementById(`new-reply-${comment.id}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isReplying, textAreaRef.current]);

  const handleChangeEditFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    let correctFiles = [];

    if (files?.length) {
      for (let i = 0; i < files?.length; i++) {
        if (isCorrectFile(files[i])) {
          correctFiles.push(files[i]);
        }
      }

      const fileArray = Array.from(correctFiles).map((file) => {
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
      });

      setAddAttachments(correctFiles);
      setAddAttachmentsPreview(fileArray);
    }
  };


  const getFullName = (firstName: string, lastName: string) => {
    if (firstName || lastName) return `${firstName} ${lastName}`;
    return false;
  };

  useEffect(() => {
    const measureHeight = () => {
      if (loadMoreRef.current) {
        const container = loadMoreRef.current.parentElement;
        if (container) {
          const lastReplyElement = container.previousElementSibling;
          if (lastReplyElement && lastReplyElement.classList.contains("pb-4 w-full reply-content")) {
            setLineHeight(lastReplyElement.clientHeight);
          }
        }
      }
    };

    requestAnimationFrame(measureHeight);
  }, [replies, repliesLimit]);

  useEffect(() => {
    const measureHeight = () => {
      if (commentRef.current) {
        const closestFlexDiv = commentRef.current.closest(".flex.gap-16.w-full")
          || commentRef.current.previousElementSibling;

        if (closestFlexDiv instanceof HTMLElement) {
          setContainerHeight(closestFlexDiv.clientHeight);
        } else {
          setContainerHeight(0);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      requestAnimationFrame(measureHeight);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [replyingCommentId, repliesLimit]);

  const options: MenuProps["items"] = [
    {
      key: "1",
      label: comment?.level === 1 ? t("Gỡ thảo luận", "Gỡ thảo luận") : t("Gỡ phản hồi", "Gỡ phản hồi"),
      icon: <DeleteOutlined />,
      className: "dropdown-delete",
      danger: true,
    },
    ...comment?.level === 1 ? [{
      key: "2",
      label: discussionMode !== "4" ? t("Quan tâm", "Quan tâm") : t("Bỏ quan tâm", "Bỏ quan tâm"),
      icon: <HeartOutlined />

    }] : []
  ];

  const handleRemoveDiscussion = async (comment: any) => {
    try {
      await removeDiscussion(comment?.id)
      message.success(`${comment?.level === 1 ? t("Gỡ thảo luận", "Gỡ thảo luận") : t("Gỡ phản hồi", "Gỡ phản hồi")} thành công`)
      setReloadDiscussion(!reloadDiscussion)
    } catch (error) {
    }
  }

  const handleSaveDiscussion = async (comment: any) => {

    try {
      const apiUrl = discussionMode !== "4" ? saveDiscussion : unSaveDiscussion
      await apiUrl(comment?.discussionId)
      setReloadDiscussion(!reloadDiscussion)
      message.success(`${discussionMode !== "4" ? t("Quan tâm", "Quan tâm") : t("Bỏ quan tâm", "Bỏ quan tâm")} thảo luận thành công`)
    } catch (error) {

    }
  }

  const handleSeenComment = async () => {
    try {
      await seenComment(comment?.discussionId)
      setReloadDiscussion(!reloadDiscussion)
    } catch (error) {
    }
  }


  //===============
  const handleClickReply = (comment: CommentProps) => {
    +  onClickReply(comment);
    +  setIsReplying(!isReplying);

  };

  const handleClickEdit = (comment: CommentProps | undefined) => {
    setEditAttachments(comment?.attachments);
    setEditText(comment?.content as string);
    onClickEdit(comment);
  };

  const handleSaveEditComment = (reply: ReplyValue) => {
    onSaveEditComment(reply);
    setEditText("");
    setEditAttachments([]);
    setAddAttachments([]);
    setAddAttachmentsPreview([]);
    setAttachmentsRemoved([]);
    onClickEdit(undefined);
  };

  const handleLike = (comment: CommentProps) => {
    onLike(comment);
  };

  const handleDislike = (comment: CommentProps) => {
    onDislike(comment);
  };
  const handleDelete = (comment: CommentProps) => {
    onDelete(comment);
  };

  return (
    <div className={`lecturer-comment pl-2 ${discussionMode === "0" ? "pointer" : ""}`}
      onClick={discussionMode === "0" ? handleSeenComment : undefined}>
      <div className="flex gap-16 w-full"
        ref={commentRef}>
        <div className="root-comment">
          <img
            className="avt-comment"
            src={
              comment.avatar ||
              `${process.env.PUBLIC_URL}/images/comment-avatar.png`
            }
            alt="avt"
          />
          {replies && replies.length > 0 && (
            <div className="tree-line-horizontal-wrap">
              <div className="tree-line-horizontal"></div>
            </div>
          )}
        </div>
        {isEditing ? (
          <div className="edit-comment w-full">
            <div className="reply-input-text">
              <Input.TextArea
                value={editText}
                placeholder={t("Nội dung thảo luận của bạn", "Nội dung thảo luận của bạn")}
                autoSize={{ minRows: 3 }}
                onChange={(e) => setEditText(getCleanText(e.target.value))}
              />
              <label htmlFor="reply-upload" className="reply-icon-upload">
                <FileTextOutlined className="pointer" />
              </label>
              <input
                onChange={handleChangeEditFiles}
                type="file"
                multiple
                id="reply-upload"
                style={{ display: "none" }}
              />
            </div>
            {(editAttachments?.length > 0 ||
              addAttachmentsPreview?.length > 0) && (
                <div className="files-reply-list">
                  {editAttachments.map((file: any) => (
                    <UploadItem
                      key={file?.id}
                      file={{
                        name: file.fileName,
                        preview: file.mainKey,
                        type: file.attachmentType,
                      }}
                      hasDelete={true}
                      onDelete={(fileName: string) => {
                        setEditAttachments((prevState: any) =>
                          prevState?.filter(
                            (item: any) => item?.fileName !== fileName
                          )
                        );
                        setAttachmentsRemoved((prevState: number[]) => [
                          ...prevState,
                          file?.id,
                        ]);
                      }}
                    />
                  ))}
                  {addAttachmentsPreview.map((file: any, index: number) => (
                    <UploadItem
                      key={`edit-${file?.name}`}
                      file={file}
                      hasDelete={true}
                      onDelete={(fileName: string) =>
                        setAddAttachmentsPreview((prevState: any) =>
                          prevState?.filter(
                            (item: any) => item?.name !== fileName
                          )
                        )
                      }
                    />
                  ))}
                </div>
              )}
            <div className="flex gap-16">
              <Button
                className="btn-primary mt-1"
                onClick={() => {
                  handleSaveEditComment({
                    file: addAttachments,
                    replyText: editText,
                    comment: comment,
                    attachmentRemoved: attachmentsRemoved,
                  });
                }}
              >
                {t("Lưu", "Lưu")}
              </Button>
              <Button
                className="btn-danger mt-1"
                onClick={() => {
                  setEditText("");
                  onClickEdit(undefined);
                  setAddAttachmentsPreview([]);
                  setAttachmentsRemoved([]);
                }}
              >
                {t("Huỷ", "Huỷ")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="pb-4 w-full reply-content">
            <div className="cmt-head flex align-center justify-space-between mb-1">
              <div>
                <span className="text-user-name">
                  {getFullName(
                    comment?.author?.firstName as string,
                    comment?.author?.lastName as string
                  ) ||
                    comment.author?.username ||
                    comment.author?.name ||
                    comment.author?.email ||
                    t("Không có tên", "Không có tên")}
                </span>
                <span className="text-time ml-1">
                  {displayDateComment(comment.createdDate || "")}
                </span>
              </div>
              <div className="flex align-center gap-16">
                {comment?.isMine ? (
                  <div className="flex align-center">
                    <span
                      className="text-primary text-14 pointer"
                      onClick={() => handleClickEdit(comment)}
                    >
                      {t("Chỉnh sửa", "Chỉnh sửa")}
                    </span>
                    <span
                      className="text-danger text-14 ml-5 mr-2 pointer"
                      onClick={() => handleDelete(comment)}
                    >
                      {t("Xóa", "Xóa")}
                    </span>
                  </div>
                ) : (
                  ""
                )}
                <Dropdown
                  menu={{
                    items: options,
                    onClick: ({ key }) => {
                      if (key === "1") {
                        Modal.confirm({
                          title: `${t("Bạn có chắc chắn muốn", "Bạn có chắc chắn muốn")} ${comment?.level === 1 ? t("Gỡ thảo luận", "Gỡ thảo luận") : t("Gỡ phản hồi", "Gỡ phản hồi")} ${t("của", "của")} ${comment.author?.fullName}`,
                          content: comment.content,
                          onOk: () => handleRemoveDiscussion(comment),
                          okText: comment?.level === 1 ? t("Gỡ thảo luận", "Gỡ thảo luận") : t("Gỡ phản hồi", "Gỡ phản hồi"),
                          cancelText: t("Huỷ", "Huỷ"),
                          centered: true,
                          icon: <CopyOutlined style={{ color: "#1677FF" }} />,
                          footer: (_, { OkBtn, CancelBtn }) => (
                            <>
                              <CancelBtn />
                              <OkBtn />
                            </>
                          ),
                        });
                      }
                      if (key === "2") {

                        handleSaveDiscussion(comment);
                      }
                    },
                  }}
                  placement="bottomRight"
                >
                  <Button>
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              </div>
            </div>
            <div
              className={`comment-content flex-1 ${isViewFull ? "h-full" : ""}`}
            >
              <span className="text-content">
                {renderHighlightedText(
                  comment?.content as string,
                  comment?.author?.username as string
                )}
              </span>
              <div className="comment-attachments">
                {attachments?.map((item: AttachmentProps) => (
                  <UploadItem
                    canDownload
                    key={item.id}
                    file={{
                      name: item.fileName,
                      preview: item.mainKey,
                      type: item.attachmentType,
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="comment-action flex gap-8 mt-1 align-center">
              <span className="pointer" onClick={() => handleLike(comment)}>
                {isLike ? <LikeActiveIcon /> : <LikeIcon />}
              </span>
              {countLike}
              <span className="pointer" onClick={() => handleDislike(comment)}>
                {isDislike ? <DislikeActiveIcon /> : <DislikeIcon />}
              </span>
              {countDislike} <FeedbackIcon />
              <span className="text-12">
                {
                  comment?.replies?.filter((reply: any) => !reply?.isReplying)
                    ?.length
                }
              </span>
              <span
                className="text-primary text-14 pointer ml-2"
                onClick={() =>
                  handleClickReply(comment)
                }
              >
                {t("Phản hồi", "Phản hồi")}
              </span>
            </div>
          </div>
        )}
      </div>

      {replies && replies.length > 0 && (
        <div className={`comment-replies replies-${comment?.level}`}
        >
          {visibleReplies.map((reply: CommentProps, index: number) => {
            return (
              <div className="comment-reply" key={reply.id}>
                <Reply
                  comment={reply}
                  replies={reply.replies}
                  onClickReply={onClickReply}
                  onSaveReply={onSaveReply}
                  onSaveEditComment={onSaveEditComment}
                  commentEditing={commentEditing}
                  onClickEdit={onClickEdit}
                  onLike={onLike}
                  onDislike={onDislike}
                  onDelete={onDelete}
                  replyingCommentId={replyingCommentId}
                />
                {index < visibleReplies.length - 1 && (
                  <div className="reply-line-horizontal"></div>
                )}
              </div>
            );
          })}

          {hasMoreReplies && replies.length > 0 ? (
            <div className="comment my-3">
              <div
                className="reply-load-more text-primary pointer pl-2 comment-reply-more"
                onClick={() => setRepliesLimit((prev) => prev + 10)}
                ref={loadMoreRef}
              >
                {t("Xem thêm", "Xem thêm")} {replies.length - repliesLimit} {t("phản hồi...", "phản hồi...")}
                {repliesLimit !== 0 && <div
                  className="reply-line-more"
                  style={{ height: `${lineHeight + 20}px` }}
                />}
              </div>
            </div>
          ) : null}
        </div>
      )}
      {replyingCommentId === (comment.level === 3 ? comment.parent : comment.id) && <div className={`comment-replies-new replies-new-${comment?.level}`}  >
        <div className="comment-reply-new">
          <img
            className="avt-reply"
            src={userInfo?.anhDaiDien || ""}
            alt="avt"
          />
          <div className="flex-1">
            <div className="reply-input-text">
              <Input.TextArea
                ref={textAreaRef}
                value={replyText}
                autoSize={{ minRows: 2 }}
                onChange={(e) =>
                  setReplyText(
                    removeDuplicate(
                      e.target.value,
                      `@${comment?.author?.username} `
                    )
                  )
                }
                id={`new-reply-${comment.id}`}
              />
              <label
                htmlFor="reply-upload"
                className="reply-icon-upload"
              >
                <FileTextOutlined className="pointer" />
              </label>
              <input
                onChange={handleChangeFiles}
                type="file"
                multiple
                id="reply-upload"
                style={{ display: "none" }}
              />
            </div>

            <div className="flex justify-end">
              <Button
                className="btn-primary mt-1"
                onClick={() =>
                  handleSaveReply({
                    comment: comment,
                    replyText: replyText,
                    file: discussionPayloadFiles,
                  })
                }
              >
                {t("Trả lời", "Trả lời")}
              </Button>
            </div>
          </div>
        </div>
        <div className="reply-line-more" style={{ height: `${containerHeight + 10}px` }} />

      </div>
      }
    </div>
  );
};

export default Reply;
